import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, LogOut, ChevronDown, Menu, Settings } from 'lucide-react';
import { useAuthStore } from '../../store/auth.store';

interface TopBarProps {
  onMenuClick?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-neutral-200/60 px-4 md:px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          {onMenuClick && (
            <button
              type="button"
              onClick={onMenuClick}
              className="md:hidden p-2.5 -ml-2 text-neutral-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          )}

          <div>
            <h2 className="text-sm sm:text-base md:text-lg font-semibold text-neutral-800">
              Welcome back, <span className="text-primary-600">{user?.username}</span>
            </h2>
            <p className="text-xs text-neutral-500 hidden sm:block">Manage your transport operations</p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button className="relative p-2.5 text-neutral-500 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30">
            <Bell size={20} />
            <span className="absolute top-2 right-2 w-2 h-2 bg-error-500 rounded-full ring-2 ring-white"></span>
          </button>

          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 hover:bg-neutral-100 rounded-xl transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
            >
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md">
                <User size={18} className="text-white" />
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-semibold text-neutral-800">{user?.username}</p>
                <p className="text-xs text-neutral-500">{user?.roles[0]}</p>
              </div>
              <ChevronDown size={16} className="text-neutral-400 hidden sm:block" />
            </button>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-elevated border border-neutral-200/60 py-2 z-20 animate-scale-in">
                  <div className="px-4 py-3 border-b border-neutral-100">
                    <p className="text-sm font-semibold text-neutral-800">{user?.username}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {user?.roles.map((role) => (
                        <span key={role} className="px-2 py-0.5 bg-primary-50 text-primary-600 text-[10px] font-semibold rounded-md">
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <User size={16} className="text-neutral-400" />
                      <span>My Profile</span>
                    </button>
                    <button
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <Settings size={16} className="text-neutral-400" />
                      <span>Settings</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error-600 hover:bg-error-50 transition-colors"
                    >
                      <LogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
